import * as React from 'react';
import type { Question } from '@mentimeter/http-clients';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Button } from '@mentimeter/ragnar-ui/button';
import { Markdown } from '@mentimeter/ragnar-ui/markdown';
import {
  ModalBody,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalRoot,
} from '@mentimeter/ragnar-ui/modal';
import {
  RadioGroup,
  RadioGroupItemList,
} from '@mentimeter/ragnar-ui/radio-group';
import { Text } from '@mentimeter/ragnar-ui/text';
import { ThemeMentiLight } from '@mentimeter/ragnar-ui/themes';
import { Tooltip } from '@mentimeter/ragnar-ui/tooltip';
import { useTranslate } from '../../../state/translation';
import { useToolbarModals } from '../toolbar-modals';
import { useAllRespondents, usePace, useQuestion } from '../../data-mediation';
import { useSlideActions } from '../../misc/slide-actions';
import { usePresentationActions } from '../../misc/presentation-actions';

enum ResetMode {
  PRESENTATION = 'presentation',
  SLIDE = 'slide',
}

const C = ({
  show,
  toggleResultsModal,
  resetPresentationResults,
  resetSlideResults,
  disableResetSlideResults,
  resetResultsFromSlide,
  questionPublicKey,
}: {
  show: boolean;
  toggleResultsModal: () => void;
  resetPresentationResults: () => void;
  resetSlideResults: (placement: 'Toolbar' | 'Controls') => void;
  disableResetSlideResults: boolean;
  resetResultsFromSlide: boolean;
  questionPublicKey: Question['public_key'];
}) => {
  const translate = useTranslate();
  const { actions } = usePace();
  React.useEffect(() => {
    setResetMode(
      resetResultsFromSlide && !disableResetSlideResults
        ? ResetMode.SLIDE
        : ResetMode.PRESENTATION,
    );
  }, [resetResultsFromSlide, disableResetSlideResults]);

  const [resetMode, setResetMode] = React.useState(ResetMode.PRESENTATION);

  const handleResetResults = React.useCallback(() => {
    switch (resetMode) {
      case ResetMode.PRESENTATION:
        resetPresentationResults();
        actions.resetStep('Reset results presentation');
        actions.resetAllSlideStates('Reset results presentation');
        toggleResultsModal();
        break;
      case ResetMode.SLIDE:
      default:
        resetSlideResults(resetResultsFromSlide ? 'Controls' : 'Toolbar');
        actions.resetStep('Reset results presentation');
        actions.resetSlideState(
          questionPublicKey,
          'Reset results presentation',
        );
        toggleResultsModal();
        // Delay resetting reset mode until the modal has been closed
        setTimeout(() => setResetMode(ResetMode.PRESENTATION), 500);
        break;
    }
  }, [
    resetMode,
    resetPresentationResults,
    actions,
    toggleResultsModal,
    resetSlideResults,
    resetResultsFromSlide,
    questionPublicKey,
  ]);

  return (
    <ModalRoot open={show} onOpenChange={toggleResultsModal}>
      <ModalContainer>
        <ThemeMentiLight>
          <Box bg="bg">
            <ModalHeader>{translate('actions.reset_results')}</ModalHeader>
            <ModalBody>
              <Text mb="space6">
                {translate('toolbar.this_will_reset_results')}
              </Text>
              <Box width="100%">
                <RadioGroup
                  value={resetMode}
                  name="results"
                  onChange={(v) => setResetMode(v)}
                  width="100%"
                  aria-label="Choose how to handle results"
                >
                  <RadioGroupItemList value={ResetMode.PRESENTATION}>
                    <Markdown>
                      {translate('toolbar.reset_presentation')}
                    </Markdown>
                  </RadioGroupItemList>
                  <Tooltip
                    trigger={
                      <RadioGroupItemList
                        value={ResetMode.SLIDE}
                        disabled={disableResetSlideResults}
                      >
                        <Markdown>{translate('toolbar.reset_slide')}</Markdown>
                      </RadioGroupItemList>
                    }
                    side="bottom"
                  >
                    {disableResetSlideResults &&
                      translate('toolbar.has_no_results')}
                  </Tooltip>
                </RadioGroup>
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button
                size="large"
                variant="secondary"
                onClick={toggleResultsModal}
              >
                {translate('actions.cancel')}
              </Button>
              <Button
                size="large"
                variant="primary"
                onClick={handleResetResults}
              >
                {translate('actions.reset_results')}
              </Button>
            </ModalFooter>
          </Box>
        </ThemeMentiLight>
      </ModalContainer>
    </ModalRoot>
  );
};

export const ResetResultsModal = () => {
  const { showResultsModal, toggleResultsModal, resetResultsFromSlide } =
    useToolbarModals();
  const { resetResults: resetPresentationResults } = usePresentationActions();
  const { resetResults: resetSlideResults } = useSlideActions();
  const respondents = useAllRespondents();
  const { public_key: questionPublicKey } = useQuestion();

  return (
    <C
      show={showResultsModal}
      toggleResultsModal={toggleResultsModal}
      resetPresentationResults={resetPresentationResults}
      resetSlideResults={resetSlideResults}
      disableResetSlideResults={!respondents}
      resetResultsFromSlide={resetResultsFromSlide}
      questionPublicKey={questionPublicKey}
    />
  );
};
